import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import { IBranch } from "../../interfaces/IBranch";
import { IBranchGroup } from "../../interfaces/IBranchGroup";
import { IService } from "../../interfaces/IService";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ELanguage } from "../../enums/ELanguage";
import msLocale from "date-fns/locale/ms";
import engbLocale from "date-fns/locale/en-GB";
import {
  PickersDay,
  PickersDayProps,
  StaticDatePicker,
} from "@mui/x-date-pickers";
import moment from "moment";
import { CApiRestful } from "../../api/CApiRestful";
import { CDataUtil } from "../../utils/CDataUtil";
import useMediaQuery from "@mui/material/useMediaQuery";
import { EAppointmentType } from "../../enums/EAppointmentType";

const TAG = "ModApptDetail";

const localeMap = {
  ms: msLocale,
  engb: engbLocale,
};

interface IProps {
  selectedLanguage: string;
  selectedServiceNo: number;
  selectedBranchCode?: string;
  selectedApptDate?: Date;
  jsonWsGetServiceListResp: any;
  jsonWsGetBranchListResp: any;
  onChange?: (
    branchCode?: string,
    apptDate?: Date,
    apptTime?: string,
    appointmentType?: string
  ) => void;
  onError?: (err: any) => void;
}

const useStyles = makeStyles({
  root: {},
  normalDay: {
    width: "40px",
    height: "35px",
    borderRadius: "8px",
    marginLeft: "4px",
    marginRight: "4px",
    marginTop: "3px",
    marginBottom: "3px",
  },
  normalWeekendDay: {
    width: "40px",
    height: "35px",
    borderRadius: "8px",
    marginLeft: "4px",
    marginRight: "4px",
    marginTop: "3px",
    marginBottom: "3px",
    opacity: 0.2,
  },
  selectableDay: {
    width: "40px",
    height: "35px",
    backgroundColor: "#F2F2F2",
    borderRadius: "8px",
    marginLeft: "4px",
    marginRight: "4px",
    marginTop: "3px",
    marginBottom: "3px",
    cursor: "pointer",
  },
  notThisMonthDay: {
    width: "40px",
    height: "35px",
    borderRadius: "8px",
    marginLeft: "4px",
    marginRight: "4px",
    marginTop: "3px",
    marginBottom: "3px",
  },
  selectedDay: {
    width: "40px",
    height: "35px",
    color: "#000000",
    backgroundColor: "#FECC00",
    borderRadius: "8px",
    marginLeft: "4px",
    marginRight: "4px",
    marginTop: "3px",
    marginBottom: "3px",
    cursor: "pointer",
  },
});

const CustomizedStaticDatePicker = styled(StaticDatePicker)(({ theme }) => {
  const isDesktop = useMediaQuery("(min-width:600px)");
  const isTablet = useMediaQuery("(min-width:300px)");

  return `
      .MuiDayPicker-weekDayLabel {
        width: 48px;
        font-size: 12px;
        color: #767676;
      }
      .MuiPickersCalendarHeader-label {
        font-weight: bold;
        font-size: 18px;
      }
      .MuiPickerStaticWrapper-content {
        min-width: ${isDesktop ? "320px" : isTablet ? "280px" : "240px"};
      }
      .MuiCalendarPicker-root {
        width: ${isDesktop ? "320px" : isTablet ? "280px" : "240px"};
        margin: ${isDesktop ? "auto" : isTablet ? "auto" : 0};
      }
    `;
});

const ModApptDetail = (props: IProps) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const upcomingAppointmentDay = (window as any).app.config
    .UpcomingAppointmentDay as number;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(
    EAppointmentType.NONE
  );
  const [selectedBranchCode, setSelectedBranchCode] = useState<string>("0");
  const [filteredBranchList, setFilteredBranchList] = useState<IBranch[]>([]);
  const [serviceList, setServiceList] = useState<IService[]>([]);
  const [appointmentType, setAppointmentType] = useState("---");
  const [selectedApptDate, setSelectedApptDate] = useState<Date | null>(null);
  const [viewingMonth, setViewingMonth] = useState<any>(undefined);
  const [apptTimeList, setApptTimeList] = useState<string[]>([]);
  const [selectedApptTime, setSelectedApptTime] = useState<string | undefined>(
    undefined
  );
  const [apptTimeError, setApptTimeError] = useState("---");

  useEffect(() => {
    setIsLoading(false);
    setSelectedApptTime(undefined);
    setApptTimeList([]);
    setApptTimeError("---");

    if (props.selectedBranchCode) {
      setSelectedBranchCode(props.selectedBranchCode);
    }

    if (props.selectedApptDate) {
      setSelectedApptDate(props.selectedApptDate);
    }

    refreshServiceGroupList();
  }, [props.selectedServiceNo]);

  useEffect(() => {
    if (serviceList && serviceList.length > 0) {
      refreshBranchList();
    }
  }, [serviceList]);

  useEffect(() => {
    if (selectedAppointmentType && serviceList && serviceList.length > 0) {
      refreshBranchList();
    }
  }, [selectedAppointmentType]);

  useEffect(() => {
    if (selectedBranchCode && selectedBranchCode !== "0") {
      const virtualBranchList = (window as any).app.config.VirtualBranch;

      var _appointmentType;
      if (virtualBranchList.includes(selectedBranchCode)) {
        _appointmentType = t("makeAppt.apptTypeVirtual");
        setSelectedAppointmentType(EAppointmentType.VIRTUAL);
      } else {
        _appointmentType = t("makeAppt.apptTypeBranch");
        setSelectedAppointmentType(EAppointmentType.BRANCH);
      }

      setAppointmentType(_appointmentType);
      if (props.onChange) {
        props.onChange(undefined, undefined, undefined, _appointmentType);
      }
    }

    if (
      props.selectedServiceNo &&
      selectedBranchCode !== "0" &&
      selectedApptDate
    ) {
      fetchApptFreeSlotList();
    }
  }, [selectedBranchCode, selectedApptDate]);

  const refreshServiceGroupList = () => {
    try {
      const _serviceList: IService[] = [];
      for (let service of props.jsonWsGetServiceListResp.GetServiceListResp
        .ServiceList.Service) {
        var serviceName = service.IndService.ServiceName;
        const serviceNo = service.IndService.ServiceNo;
        const languageStringList = service.LanguageStringList?.LanguageString;

        const displayStringValue =
          CDataUtil.extractDisplayStringValueByLanguageId(
            languageStringList,
            props.selectedLanguage
          );
        if (displayStringValue.length > 0) {
          serviceName = displayStringValue;
        }

        var finalBranchGroupIdList: number[] = [];
        var finalServiceGroupIdList: number[] = [];
        const serviceGrpList = service.ServiceGrpList.ServiceGrp;
        if (serviceGrpList.length > 0) {
          for (let serviceGrp of serviceGrpList) {
            const indServiceGrp = serviceGrp.IndServiceGrp;

            finalServiceGroupIdList = finalServiceGroupIdList.concat(
              parseInt(indServiceGrp.ServiceGrpId)
            );

            const _branchGroupIdList =
              CDataUtil.extractBranchGrpList(serviceGrp);

            finalBranchGroupIdList =
              finalBranchGroupIdList.concat(_branchGroupIdList);
          }
        } else {
          const indServiceGrp = serviceGrpList.IndServiceGrp;

          finalServiceGroupIdList = finalServiceGroupIdList.concat(
            parseInt(indServiceGrp.ServiceGrpId)
          );

          const _branchGroupIdList =
            CDataUtil.extractBranchGrpList(serviceGrpList);

          finalBranchGroupIdList =
            finalBranchGroupIdList.concat(_branchGroupIdList);
        }

        _serviceList.push({
          serviceName: serviceName,
          serviceNo: serviceNo,
          branchGroupIdList: finalBranchGroupIdList,
          serviceGroupIdList: finalServiceGroupIdList,
        });
      }
      setServiceList(_serviceList);
    } catch (err: any) {
      if (props.onError) {
        props.onError(err);
      }
    }
  };

  const refreshBranchList = () => {
    try {
      const _branchGroupList: IBranchGroup[] = [];
      const _branchList: IBranch[] = [];
      if (
        props.jsonWsGetBranchListResp.GetBranchListResp.BranchList.Branch
          .length > 0
      ) {
        for (let branch of props.jsonWsGetBranchListResp.GetBranchListResp
          .BranchList.Branch) {
          var branchName = branch.IndBranch.BranchName;
          const branchCode = branch.IndBranch.BranchCode;
          const languageStringList = branch.LanguageStringList?.LanguageString;

          const displayStringValue =
            CDataUtil.extractDisplayStringValueByLanguageId(
              languageStringList,
              props.selectedLanguage
            );
          if (displayStringValue.length > 0) {
            branchName = displayStringValue;
          }

          const indBranchGrp = branch.BranchGrpList.BranchGrp.IndBranchGrp;
          if (
            !_branchGroupList.find(
              (o) => o.branchGroupId === indBranchGrp.BranchGrpId
            )
          ) {
            _branchGroupList.push({
              branchGroupId: indBranchGrp.BranchGrpId,
            });
          }

          _branchList.push({
            branchCode: branchCode,
            branchName: branchName,
            branchGroupId: indBranchGrp.BranchGrpId,
            branchGroupName: indBranchGrp.BranchGrpName,
          });
        }
      } else {
        const singleBranch =
          props.jsonWsGetBranchListResp.GetBranchListResp.BranchList.Branch;
        var branchName = singleBranch.IndBranch.BranchName;
        const branchCode = singleBranch.IndBranch.BranchCode;
        const languageStringList =
          singleBranch.LanguageStringList?.LanguageString;

        const displayStringValue =
          CDataUtil.extractDisplayStringValueByLanguageId(
            languageStringList,
            props.selectedLanguage
          );
        if (displayStringValue.length > 0) {
          branchName = displayStringValue;
        }

        const indBranchGrp = singleBranch.BranchGrpList.BranchGrp.IndBranchGrp;
        if (
          !_branchGroupList.find(
            (o) => o.branchGroupId === indBranchGrp.BranchGrpId
          )
        ) {
          _branchGroupList.push({
            branchGroupId: indBranchGrp.BranchGrpId,
          });
        }

        _branchList.push({
          branchCode: branchCode,
          branchName: branchName,
          branchGroupId: indBranchGrp.BranchGrpId,
          branchGroupName: indBranchGrp.BranchGrpName,
        });
      }

      _branchList.sort((a, b) => {
        return a.branchName.localeCompare(b.branchName);
      });

      //setBranchList(_branchList);

      const _selectedServiceNo = parseInt(props.selectedServiceNo.toString());
      if (_selectedServiceNo) {
        const selectedService = serviceList.filter(
          (service) => service.serviceNo == _selectedServiceNo
        );

        const _filteredBranchList = _branchList.filter((branch) =>
          selectedService[0].branchGroupIdList.includes(branch.branchGroupId)
        );

        // Extra filtering which is hardcoded for virtual and branch services
        const virtualOnlyService = (window as any).app.config
          .VirtualOnlyService;
        const branchOnlyService = (window as any).app.config.BranchOnlyService;
        const virtualOrBranchService = (window as any).app.config
          .VirtualOrBranchService;
        const virtualBranch = (window as any).app.config.VirtualBranch;

        var _finalFilteredBranchList: IBranch[] = [];
        if (virtualOnlyService.includes(_selectedServiceNo)) {
          // Show virtual branch only
          _finalFilteredBranchList = _filteredBranchList.filter((branch) =>
            virtualBranch.includes(branch.branchCode)
          );
        } else if (branchOnlyService.includes(_selectedServiceNo)) {
          // Show physical branch only
          _finalFilteredBranchList = _filteredBranchList.filter(
            (branch) => !virtualBranch.includes(branch.branchCode)
          );
        } else if (virtualOrBranchService.includes(_selectedServiceNo)) {
          if (selectedAppointmentType == EAppointmentType.BRANCH) {
            _finalFilteredBranchList = _filteredBranchList.filter(
              (branch) => !virtualBranch.includes(branch.branchCode)
            );
          } else if (selectedAppointmentType == EAppointmentType.VIRTUAL) {
            _finalFilteredBranchList = _filteredBranchList.filter((branch) =>
              virtualBranch.includes(branch.branchCode)
            );
          } else {
            // Show both virtual and physical branch
            _finalFilteredBranchList = _filteredBranchList;
          }
        }

        setFilteredBranchList(_finalFilteredBranchList);

        // auto select if only one branch
        if (_finalFilteredBranchList.length == 1) {
          setSelectedBranchCode(_finalFilteredBranchList[0].branchCode);
          if (props.onChange) {
            props.onChange(_finalFilteredBranchList[0].branchCode);
          }
        }
      }
    } catch (err: any) {
      if (props.onError) {
        props.onError(err);
      }
    }
  };

  const fetchApptFreeSlotList = async () => {
    try {
      if (!props.selectedServiceNo) {
        return;
      }

      setSelectedApptTime(undefined);

      const apptDateStr = moment(selectedApptDate).format("YYYY-MM-DD");

      setIsLoading(true);
      const jsonGetApptFreeSlotListResp = await CApiRestful.getApptFreeSlotList(
        selectedBranchCode,
        props.selectedServiceNo,
        apptDateStr
      );

      if (jsonGetApptFreeSlotListResp.root) {
        if (jsonGetApptFreeSlotListResp.root.ErrorList) {
          if (jsonGetApptFreeSlotListResp.root.ErrorList.Error) {
            setApptTimeError(
              jsonGetApptFreeSlotListResp.root.ErrorList.Error.Reason
            );
            setApptTimeList([]);
            return;
          }
        }
      } else if (
        jsonGetApptFreeSlotListResp.GetApptFreeSlotListResp &&
        jsonGetApptFreeSlotListResp.GetApptFreeSlotListResp.ErrorList
      ) {
        if (
          jsonGetApptFreeSlotListResp.GetApptFreeSlotListResp.ErrorList.Error
        ) {
          setApptTimeError(
            jsonGetApptFreeSlotListResp.GetApptFreeSlotListResp.ErrorList.Error
              .Reason
          );
          setApptTimeList([]);
          return;
        }
      }

      const _apptTimeList = [];
      const apptFreeSlotList =
        jsonGetApptFreeSlotListResp.GetApptFreeSlotListResp.ApptFreeSlotList
          .ApptFreeSlot;
      if (apptFreeSlotList.length > 0) {
        for (let apptFreeSlot of apptFreeSlotList) {
          const startTime = apptFreeSlot.ApptSlot.StartTime;
          const freeSlot = apptFreeSlot.FreeSlot;

          if (freeSlot > 0) {
            _apptTimeList.push(startTime);
          }
        }
      } else {
        const startTime = apptFreeSlotList.ApptSlot.StartTime;
        const freeSlot = apptFreeSlotList.FreeSlot;

        if (freeSlot > 0) {
          _apptTimeList.push(startTime);
        }
      }

      if (_apptTimeList.length === 0) {
        setApptTimeError(t("makeAppt.errorNoSlot"));
      }

      setApptTimeList(_apptTimeList);
    } catch (err: any) {
      if (props.onError) {
        props.onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const calendarRenderer = (
    day: any,
    selectedDate: any,
    pickersDayProps: any
  ) => {
    const today = new Date(new Date().toDateString());
    const maxDate = moment()
      .add(upcomingAppointmentDay - 1, "days")
      .toDate();
    var isSelectedDay = false;
    const isSelectableDay = day >= today && day <= maxDate;
    var isInCurrentMonth = true;
    const isNormalWeekendDay = day.getDay() == 0 || day.getDay() == 6;

    if (selectedDate[0]) {
      isSelectedDay =
        day.getDate() === selectedDate[0].getDate() &&
        day.getMonth() === selectedDate[0].getMonth();
    }

    if (viewingMonth) {
      isInCurrentMonth = day.getMonth() === viewingMonth.getMonth();
    } else {
      isInCurrentMonth = day.getMonth() === today.getMonth();
    }

    if (isInCurrentMonth) {
      if (isSelectedDay) {
        return (
          <Box
            display="flex"
            justifyContent={"center"}
            alignItems="center"
            fontSize="14px"
            fontWeight="bold"
            className={classes.selectedDay}
          >
            {day.getDate()}
          </Box>
        );
      } else if (isSelectableDay) {
        return (
          <Box
            display="flex"
            justifyContent={"center"}
            alignItems="center"
            fontSize="14px"
            fontWeight="bold"
            onClick={() => {
              setSelectedApptDate(day);
              if (props.onChange) {
                props.onChange(undefined, day);
              }
            }}
            className={classes.selectableDay}
          >
            {day.getDate()}
          </Box>
        );
      } else if (isNormalWeekendDay) {
        return (
          <Box
            display="flex"
            justifyContent={"center"}
            alignItems="center"
            fontSize="14px"
            className={classes.normalWeekendDay}
          >
            {day.getDate()}
          </Box>
        );
      } else {
        return (
          <Box
            display="flex"
            justifyContent={"center"}
            alignItems="center"
            fontSize="14px"
            color="#767676"
            className={classes.normalDay}
          >
            {day.getDate()}
          </Box>
        );
      }
    } else {
      return <Box className={classes.notThisMonthDay}></Box>;
    }
  };

  const isShowBranchSelection = () => {
    var isShow = false;

    try {
      const virtualOrBranchService = (window as any).app.config
        .VirtualOrBranchService;
      if (virtualOrBranchService.includes(props.selectedServiceNo)) {
        if (selectedAppointmentType != EAppointmentType.NONE) {
          isShow = true;
        } else {
          isShow = false;
        }
      } else {
        isShow = true;
      }
    } catch (err) {
      if (props.onError) {
        props.onError(err);
      }
    }

    return isShow;
  };

  return (
    <Box>
      <Box pt={5}>
        <Typography fontSize={"20px"} fontWeight={"bold"}>
          {t("makeAppt.appointmentDetails")}
        </Typography>
      </Box>

      {(window as any).app.config.VirtualOrBranchService.includes(
        parseInt(props.selectedServiceNo.toString())
      ) && (
        <Box pt={2}>
          <TextField
            select
            fullWidth
            placeholder={t("makeAppt.appointmentType")}
            value={selectedAppointmentType}
            size="small"
            onChange={(e: any) => {
              setSelectedAppointmentType(e.target.value);
              setSelectedBranchCode("0");
              if (props.onChange) {
                props.onChange("0");
              }
            }}
          >
            <MenuItem key={EAppointmentType.NONE} value={EAppointmentType.NONE}>
              {"-- " + t("makeAppt.selectAppointmentType") + " --"}
            </MenuItem>
            <MenuItem
              key={EAppointmentType.BRANCH}
              value={EAppointmentType.BRANCH}
            >
              {t("makeAppt.apptTypeBranch")}
            </MenuItem>
            <MenuItem
              key={EAppointmentType.VIRTUAL}
              value={EAppointmentType.VIRTUAL}
            >
              {t("makeAppt.apptTypeVirtual")}
            </MenuItem>
          </TextField>
        </Box>
      )}

      {isShowBranchSelection() && (
        <Box pt={2}>
          <TextField
            select
            fullWidth
            disabled={props.selectedServiceNo === undefined}
            placeholder={t("makeAppt.branch")}
            value={selectedBranchCode}
            size="small"
            onChange={(e) => {
              setSelectedBranchCode(e.target.value);
              if (props.onChange) {
                props.onChange(e.target.value);
              }
            }}
          >
            <MenuItem key={"0"} value={"0"}>
              {"-- " + t("makeAppt.selectBranch") + " --"}
            </MenuItem>

            {filteredBranchList.map((branch) => (
              <MenuItem
                key={branch.branchCode.toString()}
                value={branch.branchCode.toString()}
              >
                {branch.branchName}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {selectedBranchCode != "0" && (
        <Box>
          <Box pt={2} display="flex">
            <Box pt={1} pl={1} pr={1} sx={{ color: "text.disabled" }}>
              {t("makeAppt.appointmentType")}
            </Box>
            <Box pl={2} pt={1} fontSize="16px" color={"#000000"}>
              {appointmentType}
            </Box>
          </Box>

          <Box pt={2} pb={2}>
            <Divider />
            <Box pt={2} pl={1} pr={1} sx={{ color: "text.disabled" }}>
              {t("makeAppt.apptDate")}
            </Box>
          </Box>

          <Box
            pt={2}
            pb={1}
            border={1}
            borderColor={"#E5E5E5"}
            borderRadius={"16px"}
          >
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={
                props.selectedLanguage == ELanguage.MALAY
                  ? localeMap["ms"]
                  : localeMap["engb"]
              }
            >
              <CustomizedStaticDatePicker
                displayStaticWrapperAs="mobile"
                openTo="day"
                dayOfWeekFormatter={(day: string) => day.substring(0, 3)}
                disableHighlightToday={true}
                value={selectedApptDate}
                onChange={(newValue: any) => {
                  if (newValue) {
                    setSelectedApptDate(newValue);
                    if (props.onChange) {
                      props.onChange(undefined, newValue);
                    }
                  }
                }}
                onMonthChange={(month: any) => {
                  setViewingMonth(month);
                }}
                renderInput={(params: any) => <TextField {...params} />}
                renderDay={calendarRenderer}
                disablePast={true}
                maxDate={moment()
                  .add(upcomingAppointmentDay - 1, "days")
                  .toDate()}
                showToolbar={false}
                componentsProps={{
                  actionBar: {
                    actions: [],
                  },
                }}
                views={["day"]}
              />
            </LocalizationProvider>
          </Box>

          <Box pt={2} pl={1} pr={1} sx={{ color: "text.disabled" }}>
            {t("makeAppt.apptTime")}
          </Box>

          {isLoading ? (
            <Box p={2} textAlign={"center"}>
              <CircularProgress color="inherit" />
            </Box>
          ) : (
          <Box>
            {apptTimeList.length > 0 ? (
              <Box
                display="flex"
                sx={{ flexWrap: "wrap" }}
                justifyContent={"center"}
              >
                {apptTimeList.map((apptTime, apptTimeIndex) => (
                  <Box p={1} key={apptTimeIndex}>
                    <Button
                      style={{
                        border: "1px solid #E5E5E5",
                        borderRadius: "4px",
                        backgroundColor:
                          selectedApptTime === apptTime
                            ? "#FECC00"
                            : "transparent",
                        fontWeight:
                          selectedApptTime === apptTime ? "bold" : "normal",
                        fontSize: "16px",
                      }}
                      sx={{
                        color: "text.primary",
                        width: "140px",
                        height: "54px",
                      }}
                      onClick={() => {
                        setSelectedApptTime(apptTime);
                        if (props.onChange) {
                          props.onChange(undefined, undefined, apptTime);
                        }
                      }}
                      variant={"outlined"}
                    >
                      {moment(apptTime).format("h:mm A")}
                    </Button>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box pt={4} textAlign="center">
                {apptTimeError}
              </Box>
            )}
          </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ModApptDetail;
